import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';

const initialStreamState = {
    info: '',
    error: '',
    freezed: '',
    isLoading: false,
    audioEnabled: false,
    updateSourceState: () => {},
};

export function useCameraStreamState({
    cameraId,
    streamType,
    playStreamType: playStreamTypeOverride,
    isTabActive,
    videoOutputId,
    transactionGroupId,
    tabId,
    transactionId,
    onStreamStateUpdate,
}) {
    const [streamState, setStreamState] = useState(initialStreamState);
    const [isStreamActivated, setIsStreamActivated] = useState(false);
    const videoRef = useRef();

    const activeStreamType = playStreamTypeOverride || streamType;

    useEffect(() => {
        const videoElement = document.getElementById(videoOutputId);
        if (!videoElement) return;

        const callback = (e) => {
            const { action, ...stateUpdate } = e.detail;
            onStreamStateUpdate?.(stateUpdate);
            setStreamState((prev) => ({ ...prev, ...stateUpdate }));
            if (action === 'hardRefresh') {
                cameraStreamManager.hardRefresh(videoOutputId);
            }
        };
        videoElement.addEventListener('videoStreamState', callback);
        return () => videoElement.removeEventListener('videoStreamState', callback);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoOutputId]);

    useEffect(() => {
        if (!isStreamActivated && isStreamActivated !== 'activating') {
            setIsStreamActivated('activating');
            // a visible tile activates on the type it shows, so a tile forced to MAIN does not switch the shared camera
            // source to its base type first; a hidden one warms up on the base type
            const activateStreamType = isTabActive ? activeStreamType : streamType;
            cameraStreamManager
                .activate(cameraId, activateStreamType, videoOutputId, transactionGroupId || tabId, transactionId, isTabActive)
                .then(() => {
                    setIsStreamActivated(true);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStreamActivated, isTabActive]);

    useEffect(() => {
        const video = videoRef.current;
        return () => {
            if (video) {
                video.pause();
                video.src = '';
                video.srcObject = null;
                video.removeAttribute('src'); // Key for URLs
                video.removeAttribute('srcObject'); // Key for Streams
                video.load(); // Forces engine release
            }
        };
    }, []);

    const handleVideoStarted = useCallback(() => {
        console.log(`Video element ${videoOutputId} onPlaying cameraId=${cameraId}`);
        streamState.updateSourceState({ info: '', error: '', isLoading: false });
    }, [streamState, videoOutputId, cameraId]);

    useLayoutEffect(() => {
        if (isStreamActivated === true && isTabActive) cameraStreamManager.playStreamType(activeStreamType, videoOutputId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTabActive, isStreamActivated]);

    return { streamState, isStreamActivated, handleVideoStarted, videoRef };
}
